/**
 * User Management Page
 * CRUD operations for users with role-based access control.
 *
 * FR3: search is server-side -- the page debounces the input and passes the
 * debounced value into useUserManagement, which routes it through the
 * /api/admin/users?search= endpoint. The previous client-side matchesQuery()
 * filter is removed -- it only matched the currently-loaded page (broken on
 * datasets larger than the limit).
 */

import { useState } from 'react'
import { Loader2, AlertCircle } from 'lucide-react'
import { UsersTable } from './user-management/UsersTable'
import { UserManagementModals } from './user-management/UserManagementModals'
import { UserSearchBar } from './user-management/UserSearchBar'
import { UserPageHeader } from './user-management/UserPageHeader'
import { useUserManagement } from './user-management/use-user-management'
import { useDebouncedValue } from '../utils/use-debounced-value'

/**
 * FR3 search-input debounce -- matches the typical typing cadence and keeps
 * the backend from being hit on every keystroke. Adjust here, not at call
 * sites.
 */
const SEARCH_DEBOUNCE_MS = 250

export function UserManagementPage(): React.ReactElement {
  const [searchQuery, setSearchQuery] = useState('')
  const [filterOrg, setFilterOrg] = useState('')

  // FR3: debounce -- the backend handler treats empty strings as no filter,
  // so a fully-cleared search box re-loads the unfiltered list.
  const debouncedSearchQuery = useDebouncedValue(searchQuery, SEARCH_DEBOUNCE_MS)
  const mgmt = useUserManagement(debouncedSearchQuery)

  // Org filter remains client-side -- it operates on the (already
  // server-filtered) page of results. For platform admins this matches the
  // existing UX. The org filter could be moved to the backend in a future
  // iteration if scale requires it.
  const filteredUsers = filterOrg
    ? mgmt.users.filter((user) => user.organisationId === filterOrg)
    : mgmt.users

  if (mgmt.loading) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[var(--accent)]" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <UserPageHeader
        onAddUser={() => {
          mgmt.resetForm()
          mgmt.setShowCreateModal(true)
        }}
      />

      {mgmt.error && (
        <div className="flex items-center gap-2 rounded-lg bg-red-50 p-4 text-red-600 dark:bg-red-900/20 dark:text-red-400">
          <AlertCircle className="h-5 w-5" />
          {mgmt.error}
        </div>
      )}

      <UserSearchBar
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        filterOrg={filterOrg}
        setFilterOrg={setFilterOrg}
        showOrgFilter={mgmt.isPlatformAdmin}
        organisations={mgmt.organisations}
      />

      <UsersTable
        users={filteredUsers}
        currentUserId={mgmt.currentUserId}
        isPlatformAdmin={mgmt.isPlatformAdmin}
        actionMenuUser={mgmt.actionMenuUser}
        setActionMenuUser={mgmt.setActionMenuUser}
        getOrgName={mgmt.getOrgName}
        onEdit={mgmt.openEditModal}
        onToggleStatus={(usr) => {
          void mgmt.handleToggleStatus(usr)
        }}
        onResetPassword={mgmt.openResetPasswordModal}
        onDelete={mgmt.openDeleteModal}
      />

      <UserManagementModals mgmt={mgmt} />
    </div>
  )
}

export default UserManagementPage
